/*
 * Copyright (c) 2023-2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {ProgramRun} from './programRun';
import {GpsTiming} from './gpsProgramTiming.model';
import {ValidationModel} from './validation.model';
import {Program} from './program';

export class MomentumProgram extends ValidationModel {
  static readonly milestonePattern = /\d{5}$/;
  id: number;
  projectId: string;
  projectType: string;
  restricted: string;
  projectName: string;
  momentumId: string;
  programCode: string;
  programName: string;
  architecture: string;
  modelYear: string;
  gvdpVersion: string;
  gpdpVersion: string;
  gpsRpo: string;
  programClassification: string;
  vehicleProjectClassification: string;
  vehProjectId: string;
  program = new Program();
  programRun = new ProgramRun();
  gpsTiming = new GpsTiming();
  icdUpdateDate: Date;

  fieldRules = [
    {fieldName: 'program.programName', required: true, maxLength: 500, uniqueFieldSetName: 'programModelYear', forErrorMessage: 'Program Name'},
    {fieldName: 'modelYear', required: true, maxLength: 500, uniqueFieldSetName: 'programModelYear', forErrorMessage: 'Model Year'},
  ]

  setDetails(program) {
    this.projectId = program.PROJECT_ID;
    this.projectType = program.PROJECT_TYPE;
    this.restricted = program.RESTRICTED;
    this.projectName = program.PROJ_NM;
    this.momentumId = program.PROGRAM_ID;
    this.programCode = program.PROGRAM_CODE;
    this.programName = program.PROGRAM_NAME;
    this.architecture = program.ARCHITECTURE;
    this.modelYear = program.MODEL_YEAR;
    this.gpdpVersion = program.GPDP ?? program.GPDP_VERSION;
    this.gvdpVersion = program.GVDP ?? program.GVDP_VERSION;
    this.gpsRpo = program.PROJECT_RPO;
    this.programClassification = program.PROGRAM_CLASSIFICATION;
    this.vehicleProjectClassification = program.VEHICLE_PROJECT_CLASSIFICATION;
    this.vehProjectId = program.VEH_PROJECT_ID;

    // these fields will be populated when we are pulling programs from the db instead of momentum
    this.id = program.id;
    if (program.program) {
      this.program = program.program;
    }
    this.programRun = new ProgramRun();
    if (program.programRun) {
      this.programRun.setProgramRunFromEsciData(program.programRun);
    }
    this.gpsTiming = new GpsTiming();
    if (program.gpsTiming) {
      this.gpsTiming.setData(program.gpsTiming);
    }
    if (program.icdUpdateDate) {
      this.icdUpdateDate = new Date(program.icdUpdateDate);
    }

    Object.keys(program).forEach(field => {
      const match: RegExpExecArray | null = MomentumProgram.milestonePattern.exec(field);
      if (match) {
        this[`date${match[0]}`] = program[field];
      }
    });
  }

  // ECS identity is GPS PROJECT_ID + vehicle VEH_PROJECT_ID + GPS PROJECT_RPO, matching
  // ProgramRun.getKey() and the program_run_ecs_identity_uidx index. Two RPOs of one project and
  // vehicle are separate timings, so they get separate grid columns and separate saved runs.
  static getProgramKey(program: MomentumProgram, timingType: string): string {
    if (timingType === 'ECS') {
      return `${program.projectId ?? ''}|${program.vehProjectId ?? ''}|${program.gpsRpo ?? ''}`;
    }
    return program.projectName;
  }

  /** Dropdown grouping key: visible GPS PROJ_NM. Distinct RPOs stay as RPO options after pick. */
  static ecsProgramOptionKey(program: MomentumProgram | null | undefined): string {
    return program?.projectName || '';
  }

  get ecsOptionLabel(): string {
    return MomentumProgram.ecsProgramOptionKey(this);
  }
}
